import React, { useContext, useEffect, useState } from "react";
import { LoginContext } from "../../../../services/api/authentication-api";
import "./download-form.scss";
import {
  formatDateToShort,
  formatTimeToHHMM,
} from "../../../../util/time-helpers";
import { GetDynamicFormData } from "../../../../services/api/task-form-api";
import { PulseLoader } from "react-spinners";
import { GetSubmittedFormUser } from "../../../../services/api/task-api";
import PropTypes from "prop-types";
import { ExportDynamicTable } from "../../../../services/api/dynamic-fields-api";
import { saveAs } from "file-saver";
import dayjs from "../../../../util/dayjs-config";
import TriyoRounded from "../../../../assets/Illustrations/TriyoRounded.svg";
import TriyoWordmark from "../../../../assets/Illustrations/TriyoWordmark.svg";
import { normalizeUrl } from "../../../../components/FluentUI/utils/Fluent-Input-Url.util";

/**
 * Formats a URL field value by prepending the default protocol only if needed.
 * Uses the shared normalizeUrl utility which supports all URL schemes (http, https, ftp, mailto, tel, etc.)
 * and properly handles protocol detection.
 *
 * @param {string|*} data - The URL data value
 * @param {string} lookUpValues - The default protocol to prepend (e.g., "https://")
 * @returns {string} The formatted URL
 */
export const formatUrlField = (data, lookUpValues) => {
  // Handle null, undefined, or non-string values
  if (data == null || typeof data !== "string") return "";
  if (data.trim() === "") return "";

  // Use the shared normalizeUrl utility which handles all URL schemes
  return normalizeUrl(data, lookUpValues || "https://");
};

export const PageTemplate = (props) => {
  return (
    <div>
      {props.pageNum === 1 ? (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "35px",
          }}
        >
          <img src={TriyoWordmark} height={30} alt={"TriyoWordmark"}></img>
        </div>
      ) : (
        <div></div>
      )}
      {props.pageNum > 1 ? (
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            left: "35px",
          }}
        >
          <img
            src={TriyoRounded}
            height={15}
            width={15}
            alt={"TriyoRounded"}
            style={{ marginRight: "10px", marginBottom: "5px" }}
          ></img>
          {`PDF Report exported from TRIYO on ${formatDateToShort(
            new Date()
          )} at ${formatTimeToHHMM(new Date())}.`}
          <span
            style={{
              marginLeft: "165px",
            }}
          >
            {props.pageNum}
          </span>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

PageTemplate.propTypes = {
  pageNum: PropTypes.number.isRequired,
};

export const DownloadForm = ({ formId, taskId, taskName }) => {
  const [formData, setFormData] = useState({
    data: null,
    isLoading: true,
  });
  const user = useContext(LoginContext);
  const labels = user?.currentUser?.objectLabels;

  useEffect(() => {
    const GetForm = async () => {
      let result = await GetDynamicFormData({
        Id: formId,
        taskId: taskId,
      });
      let comment = await GetSubmittedFormUser(taskId, formId);

      setFormData((prev) => ({
        ...prev,
        data: result.data,
        comment: comment.data,
        isLoading: false,
      }));
    };
    GetForm();
  }, []);

  const DownloadExcel = async (dynamicFieldId, localLabel) => {
    let data = {
      DynamicFieldId: dynamicFieldId,
      WorkbookName: localLabel,
      ObjectId: taskId,
    };
    let result = await ExportDynamicTable(data);
    saveAs(
      result,
      data.WorkbookName +
        "-" +
        dayjs(new Date()).format("MM/DD/YYYY-h:mm a") +
        ".xlsx"
    );
  };

  return (
    <React.Fragment>
      {formData.isLoading ? (
        <div className="pulse-loader" data-testid="loading-spinner">
          <PulseLoader></PulseLoader>
        </div>
      ) : (
        <div className="download-form">
          <div className="form-top-heading">Auto-generated {labels?.FormSingular ?? "Form"} PDF Report</div>
          <div className="form-heading">
            {`${taskName} · ${formData.data.Label}`}
          </div>
          <div className="form-info">
            {formData.comment &&
              `${labels?.FormSingular ?? "Form"} submitted to TRIYO by ${
                formData.comment?.CreatedBy?.Name
              } on ${formatDateToShort(
                new Date(formData.comment?.Created)
              )} at  ${formatTimeToHHMM(new Date(formData.comment?.Created))}.`}
            <br></br>
            {`PDF Report exported by ${
              user.currentUser.profile.userNameFull
            } from TRIYO on ${formatDateToShort(
              new Date()
            )} at ${formatTimeToHHMM(new Date())}.`}
          </div>
          {formData.data.SubForms.map((subForm, index) => (
            <React.Fragment key={subForm.Id}>
              <div className="field">
                <div className="field-heading">{`Section ${index + 1}`}</div>
                <div className="field-value section-heading">
                  {subForm.Label}
                </div>
              </div>

              {subForm.Fields.map((field) => (
                <React.Fragment key={field.Id}>
                  {field.FieldType === "Table" ? (
                    <div className="field" key={field.id}>
                      {field.Table.Columns.length > 10 ? (
                        <React.Fragment>
                          <div className="field-heading">
                            {field.LocalLabel}
                          </div>
                          {field.LocalLabel ===
                          "Detailed Transaction Analysis" ? (
                            <></>
                          ) : (
                            <>
                              <div className="field-value preview-export">
                                <span>
                                  {"<Large table: submitted values are "}
                                </span>
                                <span
                                  className="hyperlink"
                                  onClick={() =>
                                    DownloadExcel(
                                      field.DynamicFieldId,
                                      field.LocalLabel
                                    )
                                  }
                                >
                                  downloadable as a separate XLS export
                                </span>
                                <span>{">"}</span>
                              </div>
                              <div className="field-value pdf-export">
                                {
                                  "<Large table: submitted values are downloadable as a separate XLS export>"
                                }
                              </div>
                            </>
                          )}
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <div className="field-heading">
                            {field.LocalLabel}
                          </div>
                          <div className="field-value">
                            <table className="download-table">
                              <thead>
                                <tr>
                                  {field.Table.Columns.map((col) => (
                                    <th key={col.Id}>{col.Name}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {field.Table.Rows.length > 0
                                  ? field.Table.Rows.map((row) => (
                                      <tr key={row.Id}>
                                        {row.Data.map((item) => (
                                          <td key={item.Id}>{item.Value}</td>
                                        ))}
                                      </tr>
                                    ))
                                  : field.Table.Columns.map((col) => (
                                      <td key={col.Name}>
                                        {"<No input made>"}
                                      </td>
                                    ))}
                              </tbody>
                            </table>
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  ) : (
                    field.DynamicFieldId === 0 ? (
                      <div className="field" key={field.Id}>
                        <div className="sub-section-heading">{field.LocalLabel}</div>
                      </div>
                    ) : (
                      <div className="field" key={field.Id}>
                        <div className="field-heading">{field.LocalLabel}</div>
                        <div className="field-value">
                          {field.FieldType === "URL" && field.Data ? (
                            <a 
                              href={formatUrlField(field.Data, field.LookUpValues)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hyperlink"
                            >
                              {formatUrlField(field.Data, field.LookUpValues)}
                            </a>
                          ) : (
                            field.Data ? field.Data : "<No input made>"
                          )}
                        </div>
                      </div>
                    )
                  )}
                </React.Fragment>
              ))}
              <span className="page-break"></span>
            </React.Fragment>
          ))}
        </div>
      )}
    </React.Fragment>
  );
};

DownloadForm.propTypes = {
  formId: PropTypes.string,
  taskId: PropTypes.number,
  taskName: PropTypes.string,
};
